import { generatePath, useParams, NavLink, Outlet } from 'react-router-dom';
import {
  IconExpand,
  PermissionDenied,
  PermissionEnum,
  SchemaFilterType,
  Table,
} from '@ui/src';
import { useAppSelector } from '@store';
import { CustomerStatus, OrganizationType } from '../types';
import { columnsItems, columnsLabels } from './variables/accounts';
import useExternalAccounts from './hooks/use-external-accounts';

import ROUTES from '../../../routes';

import cn from './styles.module.css';

const CustomerExternalAccounts = ({ type }: { type: OrganizationType }) => {
  const { customerId } = useParams();

  const { currenciesList } = useAppSelector((state) => state.root);
  const { baseInformation: corporateBaseInformation } = useAppSelector(
    (state) => state.corporateCustomers.info.general,
  );
  const { baseInformation: individualBaseInformation } = useAppSelector(
    (state) => state.individualCustomers.info.general,
  );
  const {
    externalAccounts,
    pagination,
    pspPartners,
    openPayNotificationVisible,
    externalAccountsErrorsList,
  } = useAppSelector((state) => state.customersExternalAccounts);
  const { casp } = useAppSelector((state) => state.customersDictionaries);
  const { activePermissions, paymentTypes } = useAppSelector(
    (state) => state.root,
  );

  const {
    search,
    formattedData,
    onFilter,
    filtersComponent,
    handleSubmit,
    isLoading,
    isFetching,
    filters,
    setPage,
  } = useExternalAccounts({
    externalAccounts,
    type,
    customerId,
    paymentTypes,
    casp,
    currenciesList,
    pspPartners,
    openPayNotificationVisible,
  });

  const organizationStatus =
    type === OrganizationType.INDIVIDUAL
      ? individualBaseInformation?.status
      : corporateBaseInformation?.status;

  const link =
    type === OrganizationType.INDIVIDUAL
      ? ROUTES.CUSTOMERS_INDIVIDUAL_EXTERNAL_ACCOUNTS_CREATE
      : ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_ACCOUNTS_CREATE;

  let content;

  if (
    !activePermissions?.includes(PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST)
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <>
        {activePermissions?.includes(
          PermissionEnum.PSP_INTEGRATION_ACCOUNT_ADD_ACTION,
        ) &&
          organizationStatus !== CustomerStatus.CLOSED && (
            <div className={cn.externalAddLinkWrapper}>
              <NavLink
                to={`${generatePath(link, {
                  customerId: (customerId as string) || '',
                })}${search || ''}`}
                className={cn.externalAddLink}
              >
                <IconExpand width={16} height={16} />
                Add External Account
              </NavLink>
            </div>
          )}
        <div className={cn.externalAccountsTable}>
          <Table
            columns={columnsItems}
            data={formattedData || []}
            onSort={() => {}}
            sort={{}}
            filters={filters}
            columnsLabels={columnsLabels}
            onFilterChange={(values) => {
              onFilter({
                ...values,
                organizationId: {
                  value: [customerId as string],
                  type: SchemaFilterType.STRING,
                },
              });
            }}
            filtersComponent={filtersComponent}
            handleSubmit={handleSubmit}
            isLoading={isLoading || isFetching}
            pagination={pagination}
            tableBodyClassName={isLoading || isFetching ? cn.tableLoading : ''}
            take="20"
            tableTopClassName={
              Object.keys(filters)?.length ? cn.externalWithPadding : ''
            }
            setPage={(data) => {
              const filterValues = {
                ...filters,
                organizationId: {
                  value: [customerId as string],
                  type: SchemaFilterType.STRING,
                },
              };

              setPage({ ...data, filters: filterValues });
            }}
            error={externalAccountsErrorsList}
            linkWithoutSearch
            withVerticalScroll
            withScroll
            clickable
          />
        </div>
        <Outlet />
      </>
    );
  }

  return content;
};

export default CustomerExternalAccounts;
